import { useEffect, useRef, useState, type ReactNode } from 'react'

const PULL_THRESHOLD = 72
const PULL_MAX = 110

type Props = {
  children: ReactNode
  enabled?: boolean
  onRefresh: () => void | Promise<void>
  pullingLabel?: string
  releaseLabel?: string
  refreshingLabel?: string
}

function scrollAtTop(): boolean {
  return window.scrollY <= 0 && document.documentElement.scrollTop <= 0
}

export function PullToRefresh({
  children,
  enabled = true,
  onRefresh,
  pullingLabel = 'Pull to reset',
  releaseLabel = 'Release to clear',
  refreshingLabel = 'Resetting…',
}: Props) {
  const [pullOffset, setPullOffset] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const pullOffsetRef = useRef(0)
  const touchStartY = useRef(0)
  const tracking = useRef(false)
  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh

  useEffect(() => {
    if (!enabled) return

    const onTouchStart = (event: TouchEvent) => {
      if (refreshing || !scrollAtTop() || event.touches.length !== 1) return
      touchStartY.current = event.touches[0].clientY
      tracking.current = true
    }

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking.current || refreshing) return
      if (!scrollAtTop()) {
        tracking.current = false
        pullOffsetRef.current = 0
        setPullOffset(0)
        return
      }
      const delta = event.touches[0].clientY - touchStartY.current
      if (delta <= 0) {
        pullOffsetRef.current = 0
        setPullOffset(0)
        return
      }
      const offset = Math.min(delta * 0.5, PULL_MAX)
      pullOffsetRef.current = offset
      setPullOffset(offset)
      if (offset > 10) event.preventDefault()
    }

    const finish = () => {
      if (!tracking.current) return
      tracking.current = false
      const shouldRefresh = pullOffsetRef.current >= PULL_THRESHOLD
      pullOffsetRef.current = 0
      setPullOffset(0)
      if (!shouldRefresh || refreshing) return
      setRefreshing(true)
      void Promise.resolve(onRefreshRef.current()).finally(() => setRefreshing(false))
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', finish)
    document.addEventListener('touchcancel', finish)
    return () => {
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', finish)
      document.removeEventListener('touchcancel', finish)
    }
  }, [enabled, refreshing])

  const showIndicator = refreshing || pullOffset > 12
  const readyToRelease = pullOffset >= PULL_THRESHOLD
  const label = refreshing
    ? refreshingLabel
    : readyToRelease
      ? releaseLabel
      : pullingLabel

  return (
    <>
      {enabled && showIndicator ? (
        <div
          className="pointer-events-none fixed inset-x-0 z-40 flex justify-center"
          style={{
            top: `calc(env(safe-area-inset-top, 0px) + ${refreshing ? 8 : Math.max(8, pullOffset - 36)}px)`,
          }}
          aria-live="polite"
        >
          <div className="rounded-full border border-line/80 bg-bar-raised/95 px-3 py-1.5 text-xs font-semibold text-foam-muted shadow-lg backdrop-blur-sm">
            {label}
          </div>
        </div>
      ) : null}
      <div
        style={
          pullOffset > 0 && !refreshing
            ? { transform: `translateY(${pullOffset}px)` }
            : undefined
        }
      >
        {children}
      </div>
    </>
  )
}
